<script setup>
import Buttons from '@/components/ui/Buttons.vue';
import { toLocalUrl } from '@/utils/helpers';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="fuel-types">
        <h2
          class="mb-0 fuel-types__title h2"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h2>
        <div class="fuel-types__bottom">
          <ul class="m-0 p-0 fuel-types__items">
            <li
              v-for="(item, itemIndex) in data.items"
              :key="itemIndex"
              :class="[
                'fuel-types__item',
                data?.background_color
                  ? `fuel-types__item--${data.background_color}`
                  : '',
              ]"
              data-aos="fade-up"
              :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
            >
              <p
                v-if="item.title"
                class="fuel-types__item-title h4"
                :style="`background-color: ${item.background_color};`"
                v-html="item.title"
              ></p>
              <ul class="m-0 p-0 fuel-types__item-parameters">
                <li
                  v-for="(parameter, parameterIndex) in item.parameters"
                  :key="parameterIndex"
                  class="fuel-types__item-parameter body-3-medium"
                >
                  <span
                    v-if="parameter.title"
                    class="fuel-types__item-parameter-title"
                    v-html="parameter.title"
                  ></span>
                  <span
                    v-if="parameter.value"
                    class="fuel-types__item-parameter-value"
                    v-html="parameter.value"
                  ></span>
                </li>
              </ul>
              <Button
                v-if="item?.file"
                v-slot="slotProps"
                as-child
                variant="text"
              >
                <a
                  target="_blank"
                  :href="toLocalUrl(item?.file)"
                  :class="[
                    slotProps.class,
                    'button-icon-prev',
                    data?.background_color === 'black' ? 'white' : 'black',
                    `js-button-${itemIndex}`,
                  ]"
                  class="fuel-types__item-button link-normal"
                >
                  <CIcon
                    class="button-icon-prev-icon"
                    :name="
                      data?.background_color === 'black'
                        ? 'doc-load'
                        : 'doc-load-black'
                    "
                    width="16"
                    height="16"
                  />
                  {{ $t('ukravto_group.quality_certificate') }}
                </a>
              </Button>
            </li>
          </ul>
          <Buttons
            v-if="data.buttons && data.buttons.length > 0"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            class="fuel-types__buttons"
            :buttons="data?.buttons"
            :background-color="data?.background_color"
            :buttons-prefix-class="'js-button-fuel-types'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.fuel-types {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__bottom {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    @include media-breakpoint-up(sm) {
      gap: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
  }
  &__items {
    display: grid;
    gap: 8px;
    grid-template-columns: 1fr;
    list-style: none;
    padding: 0;
    width: 100%;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 1fr);
      padding: 0 8px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
    }
  }
  &__item {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 8px;
    margin: 0;
    padding: 24px 24px 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding: 32px 32px 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(padding-left, 24);
      @include responsive-spacing(padding-right, 24);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-bottom, 16);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    &-title {
      color: $white;
      border-radius: 2px;
      margin: 0;
      padding: 3px 4px 5px;
      width: fit-content;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 2);
        @include responsive-spacing(padding-left, 4);
        @include responsive-spacing(padding-right, 4);
        @include responsive-spacing(padding-top, 3);
        @include responsive-spacing(padding-bottom, 5);
      }
    }
    &-parameters {
      display: flex;
      flex-direction: column;
      gap: 8px;
      list-style: none;
      padding: 8px 0 0 0;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-top, 8);
      }
    }
    &-parameter {
      display: grid;
      grid-template-columns: auto auto;
      gap: 8px;
      justify-content: space-between;
      margin: 0;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
      &-title {
        color: $on-surface-variant-2;
      }
      &-value {
        color: $black;
      }
    }
  }
  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    grid-area: buttons;
    @include media-breakpoint-up(md) {
      gap: 16px;
      flex-direction: row;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__button {
    width: fit-content;
  }
}
</style>
